import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Html5Qrcode } from "html5-qrcode";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ArrowLeft, ScanLine, Camera, Keyboard } from "lucide-react";
import { Input } from "@/components/ui/input";

export default function QRScan() {
  const navigate = useNavigate();
  const scannerRef = useRef(null);
  const [running, setRunning] = useState(false);
  const [manual, setManual] = useState("");
  const [error, setError] = useState("");

  const cleanupRef = useRef(Promise.resolve());
  const [attempt, setAttempt] = useState(0);
  const stop = useCallback(() => scannerRef.current?.dispose(), []);
  const handleDecoded = useCallback((text) => {
    if (scannerRef.current?.disposed) return;
    // O id vai até o fim do caminho: peças importadas têm ":" no id (ex.: import-part-…:79).
    const match = text.match(/pecas\/([^/?#\s]+)/);
    let id = match ? match[1] : text;
    try { id = decodeURIComponent(id); } catch { /* id já sem codificação */ }
    stop();
    navigate(`/pecas/${id}`);
  }, [navigate, stop]);

  useEffect(() => {
    let mounted = true;
    let scanner;
    let started = false;
    let release;
    const reader = document.getElementById("qr-reader");
    const session = { disposed: false };
    setError("");
    setRunning(false);
    // Serialize retries/StrictMode cleanup before another camera is acquired.
    const ready = cleanupRef.current.then(async () => {
      if (session.disposed) return;
      scanner = new Html5Qrcode("qr-reader");
      await scanner.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 240, height: 240 } },
        decoded => { if (!session.disposed) handleDecoded(decoded); },
        () => {}
      );
      started = true;
      if (mounted && !session.disposed) setRunning(true);
    }).catch(() => {
      if (mounted && !session.disposed) {
        setError("Não foi possível acessar a câmera. Verifique as permissões ou use o código manual.");
      }
    });
    session.dispose = () => {
      session.disposed = true;
      if (mounted) setRunning(false);
      if (!release) release = (async () => {
        // start can finish after navigation/unmount; still release its stream.
        await ready;
        try { if (started) await scanner.stop(); } catch {}
        // A late cleanup must never clear a newer screen's reader element.
        if (scanner && document.getElementById("qr-reader") === reader) {
          try { scanner.clear(); } catch {}
        }
      })();
      return release;
    };
    scannerRef.current = session;
    return () => {
      mounted = false;
      cleanupRef.current = session.dispose();
    };
  }, [handleDecoded, attempt]);

  const submitManual = () => {
    if (!manual.trim()) { toast.error("Informe o código ou link."); return; }
    handleDecoded(manual.trim());
  };

  return (
    <div className="min-h-screen almox-bg text-slate-100 p-4">
      <Button variant="ghost" onClick={() => { stop(); navigate("/operacional"); }} className="text-slate-400"><ArrowLeft className="h-4 w-4 mr-1" /> Voltar</Button>

      <div className="max-w-md mx-auto mt-4 space-y-6">
        <div className="text-center">
          <h1 className="font-display text-3xl font-bold text-slate-50 flex items-center justify-center gap-2"><ScanLine className="h-7 w-7 text-sky-400" /> Escanear QR Code</h1>
          <p className="text-slate-400 text-sm">Aponte a câmera para a etiqueta da peça.</p>
        </div>

        <div className="rounded-2xl overflow-hidden border-2 border-dashed border-sky-500/60 bg-slate-950 aspect-square relative">
          <div id="qr-reader" className="w-full h-full [&_video]:object-cover [&_video]:w-full [&_video]:h-full" />
          {!running && !error && <div className="absolute inset-0 flex items-center justify-center text-slate-500"><Camera className="h-10 w-10 animate-pulse" /></div>}
        </div>

        {error && (
          <div className="rounded-lg border border-rose-800/60 bg-rose-950/40 p-3 text-sm text-rose-200">
            {error}
            <Button size="sm" variant="secondary" className="mt-2 w-full" onClick={() => setAttempt(value => value + 1)}>Tentar novamente</Button>
          </div>
        )}

        <div className="rounded-xl border border-slate-800 bg-slate-900/50 p-4">
          <div className="flex items-center gap-2 text-sm text-slate-300 mb-2"><Keyboard className="h-4 w-4" /> Ou informe o código/ID manualmente</div>
          <div className="flex gap-2">
            <Input data-testid="input-manual-qr" value={manual} onChange={(e) => setManual(e.target.value)} placeholder="ID da peça ou link do QR" className="bg-slate-950 border-slate-700" />
            <Button data-testid="btn-manual-qr" onClick={submitManual} className="bg-sky-700 hover:bg-sky-600">Abrir</Button>
          </div>
        </div>
      </div>
    </div>
  );
}
